/** In-app user manual. Hard-coded so it versions with the release. Sections are filtered by permission. */

export const MANUAL = [
  {
    id: "getting-started",
    title: "Getting started",
    group: "Everyone",
    blocks: [
      { t: "p", v: "TMC Learning is the Therapy Management Corporation platform for assigned training, competency and compliance. Everything you complete here is kept permanently on your transcript, including the exact version of the course you took." },
      { t: "h", v: "Signing in" },
      { t: "steps", v: [
        "Go to tmclearning.therapymgmt.com.",
        "Select Sign in with Microsoft and use your TMC Microsoft 365 account — the same one you use for Outlook.",
        "If your organisation requires multi-factor authentication, approve the prompt on your phone.",
        "You land on Home, which shows what is due next.",
      ] },
      { t: "note", tone: "info", title: "No separate password", v: "TMC Learning has no password of its own. Access is controlled by your Microsoft account, so password resets and MFA are handled by TMC IT, not inside this app." },
      { t: "h", v: "Finding your way around" },
      { t: "p", v: "The left sidebar is organised into six areas. You only see the areas your role covers, so most staff see just My Learning." },
      { t: "table", head: ["Area", "What lives there"], rows: [
        ["My Learning", "Your own training: Home, My Learning, Discover, Certificates, Transcript"],
        ["My Team", "For Directors of Rehab and managers: team compliance, approvals, employee detail"],
        ["Content", "For contributors: courses, question banks, media library"],
        ["Delivery", "Who gets what training: assignments, audiences, campaigns and reminders"],
        ["Insights", "Dashboard, reports and the audit log"],
        ["Organisation", "Administration: people and roles, branding, certificates, integrations, data migration, system settings"],
      ] },
      { t: "h", v: "Viewing as" },
      { t: "p", v: "If you hold more than one role, the Viewing as control at the top of the sidebar narrows the navigation to one role at a time — useful when you want to see the platform the way your staff see it. It changes only what is shown to you; it never changes your permissions." },
      { t: "h", v: "Notifications" },
      { t: "p", v: "The bell in the header shows in-app notifications. Assignment, reminder, overdue and lock notices are also emailed to your TMC mailbox. Open the bell, then Notification preferences, to choose which emails you receive — compliance-critical notices cannot be switched off." },
    ],
  },

  {
    id: "learner-training",
    title: "Completing your training",
    group: "Learners",
    blocks: [
      { t: "h", v: "What is due, and when" },
      { t: "p", v: "Home lists your outstanding training in deadline order. Each row shows whether the course is Required (assigned to you, with a due date) or Optional (you enrolled yourself), the due date, and your progress." },
      { t: "table", head: ["Label", "Meaning"], rows: [
        ["Required", "Assigned to you. It counts towards your compliance and has a deadline."],
        ["Optional", "You chose it from Discover. Useful for your development; does not affect compliance."],
        ["Due soon", "Due within 7 days."],
        ["Overdue", "Past its due date. Your Director of Rehab sees this on their team page."],
        ["Credit given", "Completed, or satisfied by prior equivalent training approved by your manager."],
      ] },
      { t: "h", v: "How to start and finish a course" },
      { t: "steps", v: [
        "Open the course from Home or My Learning.",
        "Work through the modules top to bottom. Required items must be completed; items marked optional can be skipped.",
        "If the course is sequential, later items stay locked until the earlier required items are done.",
        "Finish every required item. The course then moves to Completed and your certificate is issued automatically.",
      ] },
      { t: "h", v: "Enrolling yourself in optional training" },
      { t: "steps", v: [
        "Open Discover in My Learning.",
        "Filter by tag, facility or discipline, or search by title.",
        "Open the course and select Enrol. It appears in My Learning as Optional.",
      ] },
      { t: "h", v: "Finding your record" },
      { t: "p", v: "Certificates holds every PDF certificate you have earned — open, download or print them at any time. Transcript is your full history including course version, score, completion date and CEU value, and can be exported for licence renewal or a survey." },
    ],
  },

  {
    id: "learner-video",
    title: "Video and interactive video",
    group: "Learners",
    blocks: [
      { t: "p", v: "Compliance videos are deliberately strict so that completion means the content was actually watched." },
      { t: "table", head: ["Behaviour", "Why" ], rows: [
        ["Skipping or fast-forwarding may be blocked", "The contributor can require that the video is genuinely watched. The progress bar shows which parts you have covered."],
        ["You must reach a minimum watched percentage", "Usually 90% or 100%. Rewinding and rewatching is always allowed."],
        ["Questions pop up on the timeline", "Interactive video pauses and asks a question. Required questions must be answered before the item can complete."],
        ["Resume", "Close the browser and come back later; playback resumes where you stopped."],
      ] },
      { t: "h", v: "Captions and transcripts" },
      { t: "p", v: "Where captions are provided, a CC indicator appears in the player controls and captions are on by default. A written transcript, if supplied, appears below the player." },
      { t: "note", tone: "warn", title: "Progress looks lower than you expect?", v: "Course progress is the average across all required items, so a half-watched video in a five-item course shows as roughly 10%. The player shows your progress in that one video; the course figure covers the whole course." },
    ],
  },

  {
    id: "learner-quiz",
    title: "Quizzes and retries",
    group: "Learners",
    blocks: [
      { t: "p", v: "A quiz is scored out of 100 and has a passing score set by the contributor, commonly 80%." },
      { t: "steps", v: [
        "Open the quiz item in the course.",
        "Answer every question. If there is a time limit it is shown and counts down on screen.",
        "Submit. You see your score immediately unless the contributor chose to release results later.",
        "If you pass, the item completes and you continue. If you fail, you may retry depending on the course settings.",
      ] },
      { t: "table", head: ["Situation", "What happens"], rows: [
        ["You fail but have attempts left", "Retry straight away. Questions and answer order may be shuffled."],
        ["You reach the maximum failed attempts", "The quiz locks. Your Director of Rehab and the administrators are notified."],
        ["Your quiz is locked", "Ask your manager to reset it from My Team, or wait for the automatic reset if one is configured."],
        ["Remediation is configured", "You are sent back to the relevant lesson before you can retry."],
      ] },
    ],
  },

  {
    id: "manager",
    title: "Managing your team",
    group: "Managers",
    perm: "team.view",
    blocks: [
      { t: "p", v: "My Team shows the staff who report to you in Microsoft Entra ID. Directors of Rehab see their facility's clinicians; regional leaders see everyone beneath them." },
      { t: "h", v: "Working your Needs attention list" },
      { t: "p", v: "The team page leads with named staff who need action rather than a wall of charts. Each row tells you what is wrong and gives you the action to fix it." },
      { t: "steps", v: [
        "Open My Team.",
        "Work down Needs attention — overdue training, locked quizzes and staff who have not started.",
        "Use Remind on a row to email that person about that specific course.",
        "Select a name to open their employee detail: every enrollment, score, attempt and certificate.",
      ] },
      { t: "h", v: "Approvals, exemptions and waivers" },
      { t: "p", v: "My Team, then Approvals, is where requests waiting on you are queued — equivalency claims, exemption requests and quiz unlock requests." },
      { t: "table", head: ["Decision", "Use it when", "Effect on compliance"], rows: [
        ["Credit given (equivalency)", "The clinician completed equivalent training elsewhere and has evidence", "Counts as complete; recorded with your name and the evidence"],
        ["Exempt", "The requirement does not apply to this person, for example a role that never touches the system in question", "Removed from their compliance figures"],
        ["Waived", "The requirement applies but is being set aside this cycle, for example extended leave", "Excluded this cycle; returns at the next cycle"],
        ["Reset quiz", "A locked quiz needs to be unlocked after coaching", "Attempts return to zero; the lock history is kept"],
      ] },
      { t: "note", tone: "info", title: "Everything is on the record", v: "Every approval, exemption, waiver and reset is written to the audit log with your name, the time and the reason you typed. Enter a reason a surveyor would accept." },
      { t: "h", v: "Reminding a whole group" },
      { t: "p", v: "For a single person use Remind on their row. To chase a whole facility or discipline, ask an administrator to run a campaign from Delivery, which handles repeat reminders and escalation to managers automatically." },
    ],
  },

  {
    id: "contributor-course",
    title: "Building a course",
    group: "Contributors",
    perm: "courses.manage",
    blocks: [
      { t: "p", v: "Content, then Courses, lists everything you can edit. A course holds one or more versions; you always edit a draft version and publish it when it is ready." },
      { t: "h", v: "Create and outline" },
      { t: "steps", v: [
        "Content, then Courses, then New course. Give it a title, code, category and learning type.",
        "In the Build tab add a module for each logical section, then add items inside it.",
        "Drag modules and items by the grip handle to reorder them.",
        "Select an item to edit it in the right-hand panel. Mark it Required or Optional.",
        "Open the Settings tab to set duration, recurrence, CEU value, prerequisites, the certificate template and whether completions must be sequential.",
      ] },
      { t: "h", v: "Item types" },
      { t: "table", head: ["Item", "Use it for"], rows: [
        ["Video / Interactive video", "Recorded training. Interactive video adds questions on the timeline."],
        ["Quiz", "A scored assessment built under Content, then Question banks."],
        ["Text lesson", "Written content, policy summaries, instructions."],
        ["PDF / Document / Presentation", "Policies, handbooks, slide decks."],
        ["Acknowledgement / Policy attestation", "A statement the clinician must read and attest to."],
        ["Survey", "Unscored feedback."],
        ["SCORM package", "Third-party courseware, usually brought in by the Migration Assistant."],
        ["External link / Web content", "Material hosted outside TMC Learning."],
      ] },
      { t: "h", v: "Video settings that matter" },
      { t: "p", v: "On a video item you control fast-forward, rewind, skip, playback speed, seek-ahead prevention, resume and the minimum completion percentage. For compliance training, leave fast-forward and skip switched off and require 90% or more." },
      { t: "note", tone: "warn", title: "Captions", v: "If a video has no .vtt captions file, the item editor shows an amber warning and the publish checklist flags it. You can still publish — captions are not enforced — but TMC's accessibility standard (WCAG 2.1 AA) expects them on compliance training." },
      { t: "h", v: "Publishing and versioning" },
      { t: "steps", v: [
        "Check the Ready to publish list at the bottom of the outline. Red items are blocking, amber items are recommendations.",
        "Select Publish version, write a change note, and choose what happens to people who already completed the old version.",
        "Publish. The previous version becomes read-only; historical completions always keep pointing at the version that was actually taken.",
      ] },
      { t: "table", head: ["Existing completions option", "Effect"], rows: [
        ["Existing completions remain valid", "Minor fix such as a typo. Nobody retakes anything."],
        ["Require everyone to retake", "Material change. Everyone is reassigned with a 30-day deadline."],
        ["Require at next annual cycle", "Content has moved on but is not urgent. People retake when their cycle comes round."],
      ] },
      { t: "note", tone: "info", title: "Editing a published course", v: "A published version cannot be edited. Create a new draft version, make the changes there, and publish it. Learners who are part-way through finish the version they started." },
    ],
  },

  {
    id: "contributor-quiz",
    title: "Quizzes and question banks",
    group: "Contributors",
    perm: "quizzes.manage",
    blocks: [
      { t: "p", v: "Content, then Question banks, holds reusable questions and the quizzes built from them. Build the quiz here, then attach it to a course as a Quiz item." },
      { t: "steps", v: [
        "Create a bank, for example HIPAA, and add questions to it with a type, prompt, options, correct answers, explanation, points and tags.",
        "Create a quiz. Either add questions inline, or draw a random set from a bank — pick the bank, how many questions to draw, and optionally restrict to certain tags.",
        "Set the passing score, time limit, attempt limits, lock behaviour and whether answers and explanations are shown.",
        "Open the course, add a Quiz item, and select this quiz from the dropdown.",
      ] },
      { t: "table", head: ["Setting", "Guidance"], rows: [
        ["Passing score", "80% is the TMC default for compliance topics."],
        ["Max failed attempts and Lock on max failures", "Three is typical. Locking forces a coaching conversation before another attempt."],
        ["Reset policy", "Manager reset keeps a human in the loop; automatic reset after a number of hours removes the manager from the loop."],
        ["Randomize questions and answers", "Switch both on whenever you draw from a bank, to discourage answer sharing."],
        ["Show correct answers", "After pass is the safest option for compliance quizzes."],
        ["Remediation item", "Send a failed clinician back to the specific lesson before their next attempt."],
      ] },
      { t: "note", tone: "info", title: "Tags and accepted answers", v: "Tag and accepted-answer boxes take a comma-separated list, for example HIPAA, Security, PHI. Type the whole list naturally; it is tokenised when you click out of the box." },
    ],
  },

  {
    id: "contributor-media",
    title: "Media library",
    group: "Contributors",
    perm: "media.upload",
    blocks: [
      { t: "p", v: "Content, then Media library, holds every uploaded video, document, image and caption file. Uploading the same file twice is detected and the existing copy is reused." },
      { t: "steps", v: [
        "Upload the file here first, or upload it directly from the item editor while building a course.",
        "Large videos upload in chunks; keep the tab open until the progress bar completes.",
        "Pick the file from the media picker in the course item editor rather than pasting a URL, so the reference stays valid.",
      ] },
    ],
  },

  {
    id: "delivery",
    title: "Assigning training",
    group: "Delivery",
    perm: "assignments.manage",
    blocks: [
      { t: "p", v: "Delivery answers one question: who has to do what, by when. Nothing a contributor publishes reaches a clinician until it is assigned here." },
      { t: "h", v: "Assignments" },
      { t: "steps", v: [
        "Delivery, then Assignments, then New assignment.",
        "Pick the course.",
        "Choose the audience — everyone, a facility, a discipline, a job title, specific people, or a saved audience.",
        "Set the deadline: a fixed date, a number of days from assignment, or a number of days from hire date for onboarding.",
        "Read the impact preview, which tells you exactly how many people will be enrolled and notified.",
        "Confirm. Those people are enrolled and emailed.",
      ] },
      { t: "note", tone: "warn", title: "Check the impact preview", v: "The preview resets whenever you change any part of the rule. Always read the final figure before you confirm — assignment emails cannot be unsent." },
      { t: "h", v: "Audiences" },
      { t: "p", v: "An audience is a saved rule such as \"all PTs at Riverview\". Audiences are dynamic: new hires who match are picked up automatically on the next sync, so onboarding assignments keep working without anyone editing them." },
      { t: "h", v: "Campaigns and reminders" },
      { t: "p", v: "A campaign drives a deadline. It can email on a schedule before the due date, nag the people who have not started, and escalate to Directors of Rehab for staff who are overdue. Use campaigns for annual compliance pushes instead of reminding people one at a time." },
    ],
  },

  {
    id: "insights",
    title: "Reports and insights",
    group: "Insights",
    anyPerm: ["reports.all", "team.reports", "audit.view"],
    blocks: [
      { t: "p", v: "Insights holds the organisation dashboard, the report builder and the audit log. What you can see is scoped to what you are responsible for." },
      { t: "h", v: "Report presets" },
      { t: "table", head: ["Preset", "Answers"], rows: [
        ["Compliance by facility", "Which facilities are behind, ready for a regional call"],
        ["Overdue training", "Who is past a deadline, with their manager, for chasing"],
        ["Completions and certificates", "Evidence for a survey or a licence renewal"],
        ["Quiz performance", "Which questions and courses people fail, so content can be fixed"],
      ] },
      { t: "steps", v: [
        "Open Insights, then Reports.",
        "Pick a preset, or build your own by choosing columns and filters.",
        "Filter by facility, discipline, manager, course, status or date range.",
        "Export to CSV or PDF, or schedule it to arrive by email.",
      ] },
      { t: "h", v: "Audit log" },
      { t: "p", v: "Insights, then Audit log, is the permanent record of who did what: assignments, approvals, exemptions, role changes, publishes, migrations and configuration changes. It is searchable and exportable, and nothing in it can be edited or deleted." },
      { t: "note", tone: "info", title: "Charts with too little data", v: "Charts are hidden and replaced with a short explanation when there are too few people to be meaningful — a chart of three clinicians misleads more than it informs." },
    ],
  },

  {
    id: "org-people",
    title: "People and roles",
    group: "Organisation",
    perm: "users.manage",
    blocks: [
      { t: "p", v: "Organisation, then People and roles, is the staff list. People, job titles, facilities, departments and manager relationships come from Microsoft Entra ID, so change those in Entra and let the sync bring them across." },
      { t: "h", v: "What you set here" },
      { t: "table", head: ["Field", "Source"], rows: [
        ["Name, email, job title, office, manager", "Entra ID — read-only here"],
        ["Facility and discipline", "SharePoint taxonomy, matched to the person"],
        ["Roles in TMC Learning", "Set here"],
        ["Tags", "Set here — a free-text list used for targeting assignments"],
        ["Hire date", "Entra ID, used by hire-date-relative deadlines"],
      ] },
      { t: "h", v: "Roles" },
      { t: "table", head: ["Role", "Can do"], rows: [
        ["Learner", "Complete their own training. Everyone has this."],
        ["Manager", "See and act on their own reports via My Team."],
        ["Contributor", "Build and publish courses, quizzes and media."],
        ["Branding", "Application identity, certificate templates, email templates."],
        ["Administrator", "Everything above plus people, delivery, reports and settings."],
        ["Super Administrator", "Administrator plus integrations, data migration and impersonation."],
      ] },
      { t: "steps", v: [
        "Find the person by name, facility or discipline.",
        "Open their row and tick the roles they need. Roles add up; nobody needs Learner removed.",
        "Save. The change is written to the audit log and takes effect on their next page load.",
      ] },
      { t: "note", tone: "warn", title: "Impersonation", v: "Super Administrators can view the platform as another user to reproduce a problem. An amber banner is shown for the whole session and every action is audited as impersonated. Use it to diagnose, never to complete someone's training." },
    ],
  },

  {
    id: "org-certificates",
    title: "Certificates and email templates",
    group: "Organisation",
    anyPerm: ["certificate_templates.manage", "email_templates.manage", "branding.manage"],
    blocks: [
      { t: "h", v: "Designing a certificate" },
      { t: "steps", v: [
        "Organisation, then Certificate templates.",
        "Select New template, or open an existing one to copy its wording.",
        "Set the name, certificate title, subtitle and body wording, the organisation name, font, primary, border and text colours.",
        "Add the logo, background and up to two signature images with names and titles.",
        "Select Preview PDF to see a real rendered certificate, then Save.",
        "Optionally mark it Default so every course without a specific choice uses it.",
      ] },
      { t: "h", v: "Linking a certificate to a course" },
      { t: "steps", v: [
        "Content, then Courses, and open the course.",
        "Open the Settings tab.",
        "Set Certificate template to the template you designed, or leave Default template.",
        "Use Issue certificate to switch certificates off entirely for a course that should not produce one.",
        "Save. The Courses list shows the chosen template in the Certificate column.",
      ] },
      { t: "note", tone: "info", title: "Verification", v: "Every certificate carries a unique code such as TMC-2026-HIPAA-A1B2C3D4 and a public verification link, so a surveyor or an external employer can confirm it without a login." },
      { t: "h", v: "Email templates" },
      { t: "p", v: "Organisation, then Email templates, controls the wording of assignment, reminder, overdue, completion and lock emails. Placeholders such as CourseName, DueDate, DaysRemaining and CourseURL are substituted when the email is sent. Send yourself a test before saving a change that goes to the whole organisation." },
      { t: "h", v: "Branding" },
      { t: "p", v: "Organisation, then Branding, sets the application title, login tagline, colours, logos, favicon and page imagery, and the header and footer used in emails. Changes apply immediately for everyone, so preview on the Live preview tab first." },
    ],
  },

  {
    id: "org-integrations",
    title: "Microsoft integrations",
    group: "Organisation",
    perm: "integrations.manage",
    blocks: [
      { t: "h", v: "Entra ID sign-in and sync" },
      { t: "p", v: "Sign-in uses Microsoft Entra ID. A scheduled sync keeps staff, job titles, offices, manager relationships and groups current, and deactivates people who have left. Organisation, then Integrations, then Entra sync, shows the last run and lets you start one by hand." },
      { t: "table", head: ["Microsoft Graph permission", "Needed for"], rows: [
        ["User.Read.All (Application)", "Reading the staff directory"],
        ["GroupMember.Read.All (Application)", "Group and membership sync"],
        ["Mail.Send (Application)", "Sending notification email"],
        ["Sites.Selected (Application)", "Reading the SharePoint taxonomy lists"],
        ["openid, profile, offline_access, User.Read (Delegated)", "Interactive sign-in"],
      ] },
      { t: "note", tone: "warn", title: "Application, not Delegated", v: "Background sync runs without a signed-in user, so it only honours Application permissions. A permission granted as Delegated is invisible to it and produces a 403. Each Application permission also needs Grant admin consent." },
      { t: "h", v: "SharePoint taxonomy" },
      { t: "p", v: "Facilities and Disciplines are owned by SharePoint lists on the TMC-Information site, not by this application. The sync replaces the local values on every run, which is why they are read-only in the LMS." },
      { t: "steps", v: [
        "Organisation, then Integrations, then SharePoint lists.",
        "Set the site hostname and site path.",
        "Pick the Facilities and Disciplines lists from the dropdowns, which list what the application can actually see on that site.",
        "Set the name column for each list, normally Title.",
        "Switch on the source-of-truth toggle, save, then select Sync now.",
      ] },
      { t: "note", tone: "info", title: "Sites.Selected needs a second step", v: "Admin consent alone is not enough for Sites.Selected. The application must also be granted the read role on the specific site. Until that is done the list dropdowns stay empty." },
    ],
  },

  {
    id: "org-migration",
    title: "Migrating from Docebo",
    group: "Organisation",
    perm: "migrations.manage",
    blocks: [
      { t: "p", v: "Organisation, then Data migration, is a numbered journey. Every stage can be repeated safely — a batch run twice does not duplicate anything." },
      { t: "steps", v: [
        "Sources: connect the Docebo tenant with its OAuth credentials.",
        "Courses: browse what exists in Docebo and choose what is worth bringing across.",
        "User mapping: match Docebo users to TMC Entra accounts by email. Unmatched histories are kept and attached later rather than discarded.",
        "New migration: pick courses, options and tags, and run a preflight check.",
        "Queue: watch progress per batch and open a batch to see each object.",
        "Review centre: resolve anything that could not be converted automatically.",
      ] },
      { t: "table", head: ["Outcome", "What it means"], rows: [
        ["Migrated", "Converted natively into a TMC Learning course, quiz or item."],
        ["Content pending", "The course shell and history came across but the media export is still being produced. Re-run the batch later to attach it."],
        ["Placeholder", "The source object has no native equivalent. Rebuild it in the Course Builder, or resolve it in the review centre."],
      ] },
      { t: "note", tone: "info", title: "History is preserved", v: "Completion history is migrated with its original dates, so annual recurrence is calculated from the real completion, not from the migration date." },
    ],
  },

  {
    id: "org-therapy-quest",
    title: "Therapy Quest integration",
    group: "Organisation",
    perm: "integrations.manage",
    blocks: [
      { t: "p", v: "Therapy Quest is the single front door for onboarding. A new starter sees their assigned TMC Learning training on their Therapy Quest dashboard and plays it there, inside an embedded TMC Learning player, so they never have to come here to watch a video." },
      { t: "note", tone: "info", title: "TMC Learning stays the record", v: "Therapy Quest only reads and launches. Watched position, interactive answers, quiz attempts, completions, certificates, compliance and the audit trail are all written here, exactly as if the clinician had used TMC Learning directly." },
      { t: "h", v: "What has to be configured" },
      { t: "table", head: ["Setting", "Value"], rows: [
        ["PARTNER_AUDIENCES", "The TMC Learning API audience Therapy Quest requests a token for"],
        ["PARTNER_CLIENT_IDS", "The Therapy Quest application id, which we allow-list"],
        ["EMBED_ALLOWED_ORIGINS", "Every Therapy Quest web origin that will frame the player, production and staging"],
        ["PUBLIC_BASE_URL", "This platform's public URL, used to build the embed link"],
        ["Webhook", "Registered under Organisation, then Integrations, pointing at the Therapy Quest endpoint"],
      ] },
      { t: "p", v: "An Entra administrator must also grant Therapy Quest permission to call the TMC Learning API and give admin consent. The full specification handed to the Therapy Quest developers lives in the repository at docs/THERAPY_QUEST_INTEGRATION.md." },
      { t: "h", v: "If a new starter sees no training in Therapy Quest" },
      { t: "steps", v: [
        "Check they exist here: Organisation, then People and roles. TMC Learning gets people from the Entra directory sync, so a brand-new account appears after the next sync.",
        "Check the training is actually assigned: Delivery, then Assignments. Nothing reaches Therapy Quest until it is assigned here.",
        "Run the Entra sync by hand if the account is new, then ask them to reload Therapy Quest.",
      ] },
    ],
  },

  {
    id: "org-settings",
    title: "System settings",
    group: "Organisation",
    perm: "settings.manage",
    blocks: [
      { t: "p", v: "Organisation, then System settings, holds the platform-wide defaults: categories and tags, default deadlines and recurrence, reminder schedules, notification defaults, compliance thresholds, retention and the taxonomy lists that SharePoint does not own." },
      { t: "note", tone: "warn", title: "These change behaviour everywhere", v: "Altering a default deadline or a reminder schedule affects every future assignment. Existing assignments keep the rules they were created with." },
    ],
  },

  {
    id: "faq",
    title: "Frequently asked questions",
    group: "Everyone",
    blocks: [
      { t: "faq", v: [
        { q: "Why does my course show 0% when I have watched part of a video?", a: "It should now show partial progress. Course progress is the average across all required items, so part of one video in a long course is a small number. If it still shows 0%, play a few seconds of the video again so the player can report your position." },
        { q: "I cannot skip forward in a video. Is that a fault?", a: "No. Contributors can switch off skipping and fast-forwarding on compliance videos. Rewinding and rewatching is always allowed." },
        { q: "My quiz is locked. What now?", a: "You reached the maximum number of failed attempts. Ask your Director of Rehab to reset it from My Team; they may want to go through the material with you first." },
        { q: "Where is my certificate?", a: "Certificates are issued automatically the moment the last required item completes, and appear under My Learning, then Certificates. If the course is complete but there is no certificate, the course may be set not to issue one — check with your administrator." },
        { q: "Can I get a certificate for training I did elsewhere?", a: "Ask your manager to record it as Credit given with your evidence. It then counts towards your compliance and appears on your transcript." },
        { q: "What is the difference between Required and Optional?", a: "Required training was assigned to you and counts towards compliance. Optional training you chose yourself from Discover and it does not affect compliance." },
        { q: "What is the difference between Exempt and Waived?", a: "Exempt means the requirement does not apply to that person at all. Waived means it applies but is being set aside for this cycle, and it returns at the next cycle." },
        { q: "I changed someone in Entra ID but TMC Learning still shows the old details.", a: "The directory sync runs on a schedule. A Super Administrator can run it immediately from Organisation, then Integrations, then Synchronize Entra now." },
        { q: "Why can I not edit a facility or discipline?", a: "Facilities and Disciplines are owned by SharePoint lists and are replaced on every sync. Edit the SharePoint list, then sync." },
        { q: "Why can I not edit a published course?", a: "Published versions are frozen so that historical completions remain provable. Create a new draft version, change it there, and publish." },
        { q: "If I publish a new version, does everyone have to retake the course?", a: "Only if you choose that. At publish time you pick between keeping existing completions, requiring everyone to retake with a 30-day deadline, or requiring it at the next annual cycle." },
        { q: "Someone completed version 1 and we are now on version 3. What does their record say?", a: "It says version 1, with the date and score. Records always point at the version actually taken." },
        { q: "How do I chase a whole facility rather than one person at a time?", a: "Use Delivery, then Campaigns and reminders. A campaign emails on a schedule, nags the people who have not started and escalates to managers for anyone overdue." },
        { q: "Will my change send email to the whole organisation?", a: "Assignments and campaigns do. Read the impact preview before confirming; it states exactly how many people will be enrolled and notified." },
        { q: "Can I undo an assignment?", a: "You can remove the enrollment, but emails already sent cannot be unsent. This is why the impact preview exists." },
        { q: "Do people see every area of the navigation?", a: "No. Areas are filtered by role, so most clinicians see only My Learning." },
        { q: "What does Viewing as do?", a: "It narrows your own navigation to one of your roles so you can see what that role sees. It does not change your permissions." },
        { q: "Are captions required?", a: "They are strongly expected on compliance training and the builder flags a video without them, but publishing is not blocked." },
        { q: "How does annual recurrence work?", a: "A recurring course reassigns itself when the interval since the last completion elapses, so a clinician who completed in March is due again the following March." },
        { q: "Someone has left TMC. Do their records disappear?", a: "No. The Entra sync deactivates their account so they cannot sign in, and their completion history and certificates are retained." },
        { q: "Can an external surveyor check a certificate?", a: "Yes. Each certificate has a unique code and a public verification link that needs no login." },
        { q: "Can I export a report on a schedule?", a: "Yes. Build the report in Insights, then Reports, and schedule it to be emailed." },
        { q: "Why is a chart missing from the dashboard?", a: "Charts are hidden when there is too little data to be meaningful, and a short explanation is shown instead." },
        { q: "How do I get a role changed?", a: "Ask an Administrator to set it in Organisation, then People and roles. Role changes are audited." },
        { q: "Does TMC Learning have its own password?", a: "No. Sign-in is entirely through your TMC Microsoft account, so password and MFA problems are handled by TMC IT." },
      ] },
    ],
  },

  {
    id: "troubleshooting",
    title: "Troubleshooting",
    group: "Everyone",
    blocks: [
      { t: "table", head: ["Symptom", "Likely cause and fix"], rows: [
        ["Sign-in fails or loops back to the login page", "Your Microsoft session is stale. Close all browser windows, sign in to Microsoft 365 first, then return. If it persists, TMC IT should confirm the account is enabled and licensed."],
        ["Microsoft Graph denied this request (403)", "The permission is missing, or it was granted as Delegated rather than Application. Add the Application permission and select Grant admin consent."],
        ["The specified list was not found (404)", "The configured list name does not match a list on that site. Pick the list from the dropdown in Integrations, then SharePoint lists."],
        ["SharePoint sync completes with no items", "Either the lists are empty, or the application can see the site but not the lists. Sites.Selected also needs the read role granted on the specific site."],
        ["Progress is not updating in a video", "Keep the tab in the foreground; progress is reported periodically. If the network dropped, rewind slightly and continue so the watched position is sent again."],
        ["A course is complete but no certificate was issued", "The course may have Issue certificate switched off, or a required item is quietly still incomplete. Reopen the course and look for the item that is not ticked."],
        ["A quiz will not let me start", "An earlier required item in a sequential course is incomplete, or the quiz is locked after too many failed attempts."],
        ["A migrated course shows Content pending", "The export of the source media had not finished. Re-run that batch from the migration queue once Docebo has produced it."],
        ["Email notifications are not arriving", "Check Organisation, then Integrations, then Notification deliveries for failures. Mail.Send must be granted as an Application permission."],
      ] },
      { t: "h", v: "Still stuck" },
      { t: "p", v: "Contact the TMC IT help desk. Quote what you were doing, the exact wording of any message on screen, and the time it happened — administrators can match that against the audit log and the delivery log." },
    ],
  },
];

/** Flattened text per section, used for search. */
export const sectionText = (s) => {
  const parts = [s.title, s.group];
  for (const b of s.blocks) {
    if (b.t === "p" || b.t === "h") parts.push(b.v);
    else if (b.t === "steps") parts.push(...b.v);
    else if (b.t === "note") parts.push(b.title, b.v);
    else if (b.t === "table") parts.push(...b.head, ...b.rows.flat());
    else if (b.t === "faq") parts.push(...b.v.flatMap((f) => [f.q, f.a]));
  }
  return parts.join(" ").toLowerCase();
};
